import { Layout } from '../../components';
import { Popconfirm, Table, Tag, message, Image } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TPage } from './interfaces';
import {
  CheckCircleTwoTone,
  DeleteTwoTone,
  EditTwoTone,
  EyeTwoTone,
  MinusOutlined,
  StopTwoTone,
} from '@ant-design/icons';
import styles from './CocktailsListPage.module.scss';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { deleteCoctail, fetchCoctails, getCoctailsList } from '../../store/coctailsSlice';
import { IIngredient } from '../../types';
import { API_URL } from '../../lib/api/index';
import { EDIT_COCKTAIL_PAGE, VIEW_COCKTAIL_PAGE } from '../../lib';

const CocktailsListPage = ({ logOut }: { logOut: () => void }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { coctails } = getCoctailsList(useAppSelector(state => state.coctailsReducer));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchCoctails({}));
    setLoading(false);
  }, []);

  const redirectTo = async (id: string, type: TPage) => {
    if (type === 'edit') {
      navigate(`${EDIT_COCKTAIL_PAGE}?id=${id}`);
    } else {
      navigate(`${VIEW_COCKTAIL_PAGE}?id=${id}`);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      width: 150,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'ID',
      width: 70,
      dataIndex: 'indexName',
      key: 'indexName',
      fixed: 'left',
      className: styles.table__cell_verticalAlign,
      sorter: (a, b) => a.indexName - b.indexName,
      render: (_i, item) => {
        return (
          <div>
            <Tag color="#000">{item?.indexName || '...'}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Image',
      dataIndex: 'media',
      key: 'media',
      width: 100,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: media => (
        <>
          {media.posterName ? (
            <>
              <Image
                width={60}
                height={'auto'}
                src={`${API_URL}/storage/image/${media.posterName}`}
              />
            </>
          ) : (
            <div>
              <MinusOutlined />
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Main ingredient',
      dataIndex: 'baseSpirit',
      key: 'baseSpirit',
      width: 150,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: baseSpirit => (
        <>
          {baseSpirit?.map((bs: string) => {
            return <div key={bs}>{bs} </div>;
          })}
        </>
      ),
    },
    {
      title: 'Ingredients',
      dataIndex: 'ingredients',
      key: 'ingredients',
      width: 200,
      className: styles.table__cell_verticalAlign,
      render: ingredients => (
        <>
          {ingredients?.map((i: IIngredient) => {
            return (
              <div key={i._id}>
                {i.description} {i.value}
                {i.unit}
              </div>
            );
          })}
        </>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: categories => (
        <>
          {categories?.map((category: string) => {
            return <div key={category}>{category}</div>;
          })}
        </>
      ),
    },
    {
      title: 'Degrees',
      width: 100,
      dataIndex: 'degrees',
      key: 'degrees',
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Strength',
      dataIndex: 'strength',
      key: 'strength',
      width: 150,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: strength => (
        <>
          {strength?.map((st: string) => {
            return <div key={st}>{st} </div>;
          })}
        </>
      ),
    },
    {
      title: 'Taste',
      dataIndex: 'flavor',
      key: 'flavor',
      width: 150,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: flavor => (
        <>
          {flavor?.map((f: string) => {
            return <div key={f}>{f} </div>;
          })}
        </>
      ),
    },
    {
      title: 'Tools',
      dataIndex: 'tools',
      key: 'tools',
      width: 230,
      className: styles.table__cell_verticalAlign,
      render: tools => (
        <>
          {tools?.map((tool: string) => {
            return (
              <Tag color={'purple'} key={tool}>
                {tool.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Additional tools',
      dataIndex: 'addTools',
      key: 'addTools',
      width: 150,
      className: styles.table__cell_verticalAlign,
      render: addTools => (
        <>
          {addTools?.map((addTool: string) => {
            return (
              <Tag color={'volcano'} key={addTool}>
                {addTool.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Glass',
      dataIndex: 'glassware',
      key: 'glassware',
      width: 200,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: glassware => (
        <>
          {glassware?.map((gw: string) => {
            return <div key={gw}>{gw}</div>;
          })}
        </>
      ),
    },
    {
      title: 'Volume',
      dataIndex: 'volume',
      key: 'volume',
      width: 100,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Type serving',
      dataIndex: 'typeServing',
      key: 'typeServing',
      width: 130,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
      width: 150,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Method',
      dataIndex: 'methodMaking',
      key: 'methodMaking',
      width: 100,
      align: 'center',
      className: styles.table__cell_verticalAlign,
      render: methods => (
        <>
          {methods?.map((method: string) => {
            return (
              <Tag color={'#2db7f5'} key={method}>
                {method.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Сolor',
      dataIndex: 'color',
      key: 'color',
      width: 100,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Garnish',
      dataIndex: 'garnish',
      key: 'garnish',
      width: 180,
      className: styles.table__cell_verticalAlign,
      render: garnishes => (
        <>
          {garnishes?.map((garnish: string) => {
            return (
              <Tag color={'lime'} key={garnish}>
                {garnish.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Type of ice',
      dataIndex: 'typeIce',
      key: 'typeIce',
      width: 150,
      className: styles.table__cell_verticalAlign,
      render: typesIce => (
        <>
          {typesIce?.map((typeIce: string) => {
            return (
              <Tag color={'#f50'} key={typeIce}>
                {typeIce.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Difficulty',
      dataIndex: 'difficulty',
      key: 'difficulty',
      width: 120,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 170,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Origin',
      dataIndex: 'origin',
      key: 'origin',
      width: 170,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Time for cocktail',
      dataIndex: 'timeForCocktail',
      key: 'timeForCocktail',
      width: 150,
      className: styles.table__cell_verticalAlign,
      render: timeForCocktail => (
        <>
          {timeForCocktail?.map((time: string) => {
            return (
              <Tag color={'#f50'} key={time}>
                {time.toUpperCase()}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Vegan free',
      dataIndex: 'isVegan',
      key: 'isVegan',
      width: 120,
      className: styles.table__cell_verticalAlign,
      render: isVegan => (
        <>
          {isVegan ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '24px' }} />
          ) : (
            <StopTwoTone twoToneColor="#cd201f" style={{ fontSize: '24px' }} />
          )}
        </>
      ),
    },
    // {
    //   title: 'Steps',
    //   dataIndex: 'cookingSteps',
    //   key: 'cookingSteps',
    //   width: 300,
    //   className: styles.table__cell_verticalAlign,
    //   render: cookingSteps => (
    //     <>
    //       {cookingSteps?.map((step: IStep, idx: number) => {
    //         return (
    //           <div key={step._id}>
    //             {' '}
    //             {idx + 1}. {step.description?.[0].value}
    //           </div>
    //         );
    //       })}
    //     </>
    //   ),
    // },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      align: 'center',
      className: styles.table__cell_verticalAlign,
    },
    {
      title: 'Action',
      key: 'operation',
      fixed: 'right',
      width: 60,
      align: 'center',
      render: coctail => {
        return (
          <div className={styles.table__actions}>
            <a href={`${VIEW_COCKTAIL_PAGE}?id=${coctail.key}`}>
              <EyeTwoTone twoToneColor="#18b222" style={{ fontSize: '18px', cursor: 'pointer' }} />
            </a>

            <a href={`${EDIT_COCKTAIL_PAGE}?id=${coctail.key}`}>
              <EditTwoTone twoToneColor="#2F58CD" style={{ fontSize: '18px', cursor: 'pointer' }} />
            </a>

            <Popconfirm
              title={`Are you sure you want to delete?`}
              onConfirm={() => confirmDelete(coctail.key)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteTwoTone twoToneColor="#D21312" style={{ fontSize: '18px' }} />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const coctailsData = coctails.map((coctail: any) => {
    return {
      indexName: coctail.indexName,
      key: coctail._id,
      name: coctail.eng.name,
      media: coctail.eng.media,
      baseSpirit: coctail.eng.baseSpirit,
      ingredients: coctail.eng.ingredients,
      category: coctail.eng.category,
      degrees: coctail.eng.degrees,
      strength: coctail.eng.strength,
      flavor: coctail.eng.flavor,
      tools: coctail.eng.tools,
      addTools: coctail.eng.addTools,
      glassware: coctail.eng.glassware,
      volume: coctail.eng.volume,
      typeServing: coctail.eng.typeServing,
      group: coctail.eng.group,
      methodMaking: coctail.eng.methodMaking,
      color: coctail.eng.color,
      garnish: coctail.eng.garnish,
      typeIce: coctail.eng.typeIce,
      difficulty: coctail.eng.difficulty,
      country: coctail.eng.country,
      origin: coctail.eng.origin,
      timeForCocktail: coctail.eng.timeForCocktail,
      isVegan: coctail.eng.isVegan,
      description: coctail.eng.description,
      operation: { ...coctail.eng, id: coctail._id },
      children: [
        {
          key: coctail._id,
          name: <Tag color="#87d068">RU:</Tag>,
          media: '-',
          baseSpirit: coctail.ru.baseSpirit,
          ingredients: coctail.ru.ingredients,
          category: coctail.ru.category,
          degrees: coctail.ru.degrees,
          strength: coctail.ru.strength,
          flavor: coctail.ru.flavor,
          tools: coctail.ru.tools,
          addTools: coctail.ru.addTools,
          glassware: coctail.ru.glassware,
          volume: coctail.ru.volume,
          typeServing: coctail.ru.typeServing,
          group: coctail.ru.group,
          methodMaking: coctail.ru.methodMaking,
          color: coctail.ru.color,
          garnish: coctail.ru.garnish,
          typeIce: coctail.ru.typeIce,
          difficulty: coctail.ru.difficulty,
          country: coctail.ru.country,
          origin: coctail.ru.origin,
          timeForCocktail: coctail.ru.timeForCocktail,
          isVegan: coctail.ru.isVegan,
          description: coctail.ru.description,
          operation: { ...coctail.eng, id: coctail._id },
        },
      ],
    };
  });

  const confirmDelete = async (id: string) => {
    setLoading(true);
    await deleteCoctail(id);
    await dispatch(fetchCoctails({}));
    setLoading(false);
    message.success('Coctail deleted successfully!');
  };

  return (
    <Layout title={<div>Сocktails list</div>} logOut={logOut} loading={loading}>
      {!loading && (
        <Table columns={columns} dataSource={coctailsData} scroll={{ x: 1500 }} bordered />
      )}
    </Layout>
  );
};

export default CocktailsListPage;
